import { useState } from "react";
import { FieldMappingCreationAndEditDict, LinkedAccount } from "../../../../../../models/Entities";
import { FieldMappingSource } from "../../../../../../constants";
import { showSuccessToast, showErrorToast } from "../../../../../shared/Toasts";
import {
  createLinkedAccountFieldMapping,
  editFieldMappingInstance,
} from "../../../../../../api-client/APIClient";
import { parseFieldTraversalPath } from "../../../../configuration/field-mappings/utils/FieldMappingUtils";
import { buildExpressionPayload } from "../utils/expressionUtils";

const useUnmappedTargetCard = () => {
  // state
  const [createdFieldMappingInstanceId, setCreatedFieldMappingInstanceId] = useState<string | null>(
    null,
  );
  const [originFieldForAdvancedMapping, setOriginFieldForAdvancedMapping] =
    useState<FieldMappingCreationAndEditDict | null>(null);
  const [showAdvancedMappingButton, setShowAdvancedMappingButton] = useState<boolean>(false);
  const [showAdvancedFieldMappingModal, setShowAdvancedFieldMappingModal] =
    useState<boolean>(false);
  const [jsonAdvancedMappingExpression, setJsonAdvancedMappingExpression] = useState<string | null>(
    null,
  );

  // event handlers
  const saveLinkedAccountMapping = (
    selectedRemoteField: FieldMappingCreationAndEditDict,
    linkedAccount: LinkedAccount,
    fieldKey: string,
    fieldDescription: string,
    commonModel: string,
    jsonAdvancedMappingExpression: string | null,
    isJmesPathDeprecationEnabled: boolean = false,
  ) => {
    const fieldMappingPayload = {
      linked_account_id: linkedAccount.id,
      common_model_id: `${linkedAccount.category}.${commonModel}`,
      field_key: fieldKey,
      field_description: fieldDescription,
      field_traversal_path: parseFieldTraversalPath(selectedRemoteField.traversal_path),
      api_endpoint_id: selectedRemoteField.api_endpoint_id,
      create_for_organization: false,
      display_name: selectedRemoteField.display_name,
      origin_type: "string",
      configured_by: FieldMappingSource.ORGANIZATION,
      ...buildExpressionPayload(jsonAdvancedMappingExpression, isJmesPathDeprecationEnabled),
    };

    createLinkedAccountFieldMapping(
      fieldMappingPayload,
      (createdFieldMappingInstanceId) => {
        if (createdFieldMappingInstanceId) {
          setCreatedFieldMappingInstanceId(createdFieldMappingInstanceId);
          showSuccessToast("Created linked account Field Mapping.");
        }
      },
      async (response) => {
        setShowAdvancedFieldMappingModal(false);
        if (response) {
          try {
            const errorData = await response.json();
            if (errorData?.advanced_mapping_expression?.[0]) {
              showErrorToast(
                `Invalid advanced mapping expression: ${errorData.advanced_mapping_expression[0]}`,
              );
            } else if (errorData?.jmes_path?.[0]) {
              showErrorToast(`Invalid JMESPath expression: ${errorData.jmes_path[0]}`);
            } else {
              showErrorToast("Unable to create field mapping");
            }
          } catch {
            showErrorToast("Unable to create field mapping");
          }
        } else {
          showErrorToast("Unable to create field mapping");
        }
      },
    );
  };

  const editCreatedFieldMapping = (
    selectedRemoteField: FieldMappingCreationAndEditDict,
    jsonAdvancedMappingExpression: string | null,
    isJmesPathDeprecationEnabled: boolean = false,
  ) => {
    const editFieldMappingPayload = {
      field_traversal_path: parseFieldTraversalPath(selectedRemoteField.traversal_path),
      field_mapping_instance_id: createdFieldMappingInstanceId!,
      api_endpoint_id: selectedRemoteField.api_endpoint_id,
      origin_type: selectedRemoteField.type,
      display_name: selectedRemoteField.display_name,
      ...buildExpressionPayload(jsonAdvancedMappingExpression, isJmesPathDeprecationEnabled),
    };
    editFieldMappingInstance(createdFieldMappingInstanceId!, editFieldMappingPayload, () => {
      showSuccessToast("Successfully edited Field Mapping");
    });
  };

  return {
    createdFieldMappingInstanceId,
    originFieldForAdvancedMapping,
    showAdvancedMappingButton,
    showAdvancedFieldMappingModal,
    setCreatedFieldMappingInstanceId,
    setOriginFieldForAdvancedMapping,
    setShowAdvancedMappingButton,
    setShowAdvancedFieldMappingModal,
    saveLinkedAccountMapping,
    editCreatedFieldMapping,
    jsonAdvancedMappingExpression,
    setJsonAdvancedMappingExpression,
  };
};

export default useUnmappedTargetCard;
